import { Input, Select } from "antd";
import _ from "lodash";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import { Button } from "@components/Button";
import { EditorAudio } from "@components/EditorComponents/EditorAudio";
import { EditorImage } from "@components/EditorComponents/EditorImage";
import { EditorRunningText } from "@components/EditorComponents/EditorRunningText";
import { EditorVideo } from "@components/EditorComponents/EditorVideo";
import { EditorWebsite } from "@components/EditorComponents/EditorWebsite";
import { EditorYoutubeLive } from "@components/EditorComponents/EditorYoutubeLive";
import { ImportAudio } from "@components/ImportComponents/ImportAudio";
import { ImportImage } from "@components/ImportComponents/ImportImage";
import { ImportVideo } from "@components/ImportComponents/ImportVideo";
import { Text } from "@components/Text";
import {
  COLOR_GREEN,
  COLOR_GREY,
  COLOR_GREY_1,
  COLOR_WHITE,
} from "@static/COLORS";
import { CONTENTS_ATTRIBUTE_ANOTHER } from "@static/CONTENTS_ATTRIBUTE_ANOTHER";
import { DEFAULT_DURATION } from "@static/DEFAULT_DURATION";

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 2px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  gap: 16px;
  overflow: hidden;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

const ContainerContents = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  flex-grow: 1;
  gap: 16px;
  height: calc(100% - 56px);
`;

const SectionShowMedia = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: scroll;
  border-radius: 6px;
  padding: 8px;
  gap: 8px;
  box-sizing: border-box;
  border: 1px solid ${COLOR_GREY};
`;

const SectionAddMedia = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 6px;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${COLOR_GREY};
`;

const ContainerFilter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionFilterSelect = styled.div`
  width: 100%;
`;

const SectionFilterInput = styled.div`
  width: 100%;
  /* border: 1px solid ${COLOR_GREY_1}; */
`;

const SectionAddMediaList = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MediaItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid ${COLOR_GREY_1};
  box-sizing: border-box;
`;

const FILTER_LIST = [
  { label: "Image", value: "image", isUseDatabase: true },
  { label: "Video", value: "video", isUseDatabase: true },
  { label: "Audio", value: "audio", isUseDatabase: true },
  { label: "Website", value: "website", isUseDatabase: false },
  // { label: "Video Streaming", value: "video-streaming", isUseDatabase: false },
  { label: "Youtube Live", value: "youtube-live", isUseDatabase: false },
  { label: "Running Text", value: "running-text", isUseDatabase: false },
];

const MAP_IS_USE_DATABASE = {
  image: true,
  video: true,
  audio: true,
  website: false,
  "video-streaming": false,
  "youtube-live": false,
  "running-text": false,
};

export const ManageBlock = ({
  adsBlocks,
  setAdsBlocks,
  campaignData,
  selectedBlock,
  blockMedias,
  setIsModalVisible,
}) => {
  const [blockMediaList, setBlockMediaList] = useState(blockMedias);
  const [filterText, setFilterText] = useState("");

  const [selectedMediaType, setSelectedMediaType] = useState(
    _.chain(FILTER_LIST).first().get(["value"]).value()
  );
  // all image form database where location_id
  const mediaList = useSelector(({ mediaList }) => {
    return _.get(mediaList, ["data"]);
  });

  const onHandleImportBlockMedias = ({ item }) => {
    setBlockMediaList((prev) => {
      return [item, ...prev];
    });
  };

  const onHandleConfirm = () => {
    const blockItem = {
      ...selectedBlock,
      blockMedias: blockMediaList,
    };
    const updateAdsBlock = _.map(adsBlocks, (item) => {
      return _.get(item, ["block_key"]) === _.get(blockItem, ["block_key"])
        ? blockItem
        : item;
    });
    setAdsBlocks(updateAdsBlock);
    setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
  };

  const onHandleFilterChange = (e) => {
    const value = _.get(e, ["target", "value"]);
    setFilterText(value);
  };

  const filteredDataSource = _.chain(mediaList)
    .get([selectedMediaType])
    .filter((item) => {
      const itemName = _.chain(item).get(["display_name"]).toLower().value();
      const lowerFilterText = _.toLower(filterText);
      return _.includes(itemName, lowerFilterText);
    })
    .value();

  const onHandleImportContentAnother = ({ type }) => {
    const contentsAttribute = _.get(CONTENTS_ATTRIBUTE_ANOTHER, [type]);
    const blockMedia = {
      type: _.get(contentsAttribute, ["type"]),
      is_use_media_in_project: false,
      media_id: null,
      duration: DEFAULT_DURATION,
      start_date: _.get(campaignData, ["campaign_start_date"]),
      end_date: _.get(campaignData, ["campaign_end_date"]),
      attribute: _.get(contentsAttribute, ["attribute"]),
    };
    onHandleImportBlockMedias({ item: blockMedia });
  };

  return (
    <Container>
      <Line />
      <InnerContainer>
        <ContainerContents>
          <SectionShowMedia>
            {_.map(blockMediaList, (item, index) => {
              const key = uuidv4();
              const type = _.get(item, ["type"]);
              switch (type) {
                case "image":
                  return (
                    <EditorImage
                      key={key}
                      item={item}
                      index={index}
                      blockMediaList={blockMediaList}
                      setBlockMediaList={setBlockMediaList}
                    />
                  );
                case "video":
                  return (
                    <EditorVideo
                      key={key}
                      item={item}
                      index={index}
                      blockMediaList={blockMediaList}
                      setBlockMediaList={setBlockMediaList}
                    />
                  );
                case "audio":
                  return (
                    <EditorAudio
                      key={key}
                      item={item}
                      index={index}
                      blockMediaList={blockMediaList}
                      setBlockMediaList={setBlockMediaList}
                    />
                  );

                case "website":
                  return (
                    <EditorWebsite
                      key={key}
                      item={item}
                      campaignData={campaignData}
                      index={index}
                      blockMediaList={blockMediaList}
                      setBlockMediaList={setBlockMediaList}
                    />
                  );
                case "youtube-live":
                  return (
                    <EditorYoutubeLive
                      key={key}
                      item={item}
                      campaignData={campaignData}
                      index={index}
                      blockMediaList={blockMediaList}
                      setBlockMediaList={setBlockMediaList}
                    />
                  );

                case "running-text":
                  return (
                    <EditorRunningText
                      key={key}
                      item={item}
                      campaignData={campaignData}
                      index={index}
                      blockMediaList={blockMediaList}
                      setBlockMediaList={setBlockMediaList}
                    />
                  );

                default:
                  return null;
              }
            })}
          </SectionShowMedia>
          <SectionAddMedia>
            <ContainerFilter>
              <SectionFilterSelect>
                <Select
                  size="large"
                  placeholder="Select a customer"
                  optionFilterProp="children"
                  style={{ width: "100%" }}
                  options={FILTER_LIST}
                  defaultValue={selectedMediaType}
                  onChange={(value) => {
                    setSelectedMediaType(value);
                    setFilterText("");
                  }}
                  onClear={() => {
                    setSelectedMediaType(null);
                  }}
                />
              </SectionFilterSelect>
              {_.get(MAP_IS_USE_DATABASE, [selectedMediaType]) && (
                <SectionFilterInput>
                  <Input
                    placeholder="Filter by name"
                    value={filterText}
                    onChange={(e) => onHandleFilterChange(e)}
                    style={{ width: "100%", height: 40 }}
                  />
                </SectionFilterInput>
              )}
            </ContainerFilter>
            <SectionAddMediaList>
              {_.get(MAP_IS_USE_DATABASE, [selectedMediaType]) &&
                _.chain(filteredDataSource)
                  // .get([selectedMediaType])
                  .map((item, index) => {
                    return (
                      <MediaItem key={index}>
                        {(() => {
                          switch (selectedMediaType) {
                            case "image":
                              return (
                                <ImportImage
                                  key={index}
                                  campaignData={campaignData}
                                  item={item}
                                  onHandleImportBlockMedias={
                                    onHandleImportBlockMedias
                                  }
                                />
                              );
                            case "video":
                              return (
                                <ImportVideo
                                  key={index}
                                  item={item}
                                  campaignData={campaignData}
                                  onHandleImportBlockMedias={
                                    onHandleImportBlockMedias
                                  }
                                />
                              );

                            case "audio":
                              return (
                                <ImportAudio
                                  key={index}
                                  campaignData={campaignData}
                                  item={item}
                                  onHandleImportBlockMedias={
                                    onHandleImportBlockMedias
                                  }
                                />
                              );

                            default:
                              break;
                          }
                        })()}
                      </MediaItem>
                    );
                  })
                  .value()}

              {!_.get(MAP_IS_USE_DATABASE, [selectedMediaType]) && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    type="button"
                    color={COLOR_GREEN}
                    padding="5px 15px"
                    onClick={() =>
                      onHandleImportContentAnother({
                        type: selectedMediaType,
                      })
                    }
                  >
                    <Text fontWeight="600" color={COLOR_WHITE}>
                      ADD
                    </Text>
                  </Button>
                </div>
              )}
            </SectionAddMediaList>
          </SectionAddMedia>
        </ContainerContents>
        <ButtonContainer>
          <Button
            onClick={() => onHandleConfirm()}
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Confirm
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </Container>
  );
};
